import React, { useMemo, useState, useEffect, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import GetAppIcon from '@mui/icons-material/GetApp';
import TableChartIcon from '@mui/icons-material/TableChart';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArticleIcon from '@mui/icons-material/Article';
import PostAddIcon from '@mui/icons-material/PostAdd';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DescriptionIcon from '@mui/icons-material/Description';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PrintIcon from '@mui/icons-material/Print';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import SaveIcon from '@mui/icons-material/Save';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import PaymentsIcon from '@mui/icons-material/Payments';
import styles from './NavigationButton.module.scss';

const LABEL_ICON_MAP = {
  Actions: PostAddIcon,
  Downloads: FileDownloadIcon,
  DownloadDocuments: FileDownloadIcon,
  DownloadDocument: GetAppIcon,
  DownloadSelectedDocuments: GetAppIcon,
  DownloadReceptionLog: FileDownloadIcon,
  DownloadExcelFile: TableChartIcon,
  DownloadExpenseAssignmentReport: GetAppIcon,
  UploadDocumentsOrAddJournalEntry: PostAddIcon,
  GenerateDocuments: ArticleIcon,
  OpenMonthReport: LockOpenIcon,
  CloseMonthReport: LockIcon,
  ManageMonthlyStatus: CalendarMonthIcon,
  Payment: PaymentIcon,
  Approval: CheckCircleIcon,
  Receipt: ReceiptIcon,
  ReceiptTax: ReceiptIcon,
  generateInvoice: ReceiptIcon,
  OpenOptions: SettingsIcon,
  CommandStatus: AssignmentTurnedInIcon,
  GenerateLedgerCard: DescriptionIcon,
  PostponedChecks: ScheduleIcon,
  PrintChecks: PrintIcon,
  Reprint: PrintIcon,
  ShowDuplicates: ContentCopyIcon,
  MultiTransferDocuments: DriveFileMoveIcon,
  ContinueMultiTransfer: DriveFileMoveIcon,
  MergeToolbarTitle: CallMergeIcon,
  BulkEdit: EditIcon,
  BulkDelete: DeleteIcon,
  AddNewJournalEntry: PostAddIcon,
  JournalEntryTemplates: DescriptionIcon,
  VatClosing: PriceCheckIcon,
  PayrollJournalEntries: PaymentsIcon,
  EditJournalEntryType: EditIcon,
  GenerateLedgerReports: FileDownloadIcon,
  ImportPaymentsFromExcel: DriveFolderUploadIcon,
  ImportMonthlyChargesFromExcel: DriveFolderUploadIcon,
  GenerateMasavFile: SaveIcon,
  PaymentArrangements: EventNoteOutlinedIcon,
  GenerationLogs: ArticleIcon,
  'SavedCards.manage': CreditCardIcon,
  'CreditCardBulkCharge.openButton': PaymentIcon,
  WithholdingTaxReports: ReceiptLongIcon,
  AddClientItem: AddCircleOutlineIcon,
  ClientItemsImportFromExcel: DriveFolderUploadIcon,
  StockIntake: FileUploadIcon,
  StockRelease: FileDownloadIcon,
  ItemsMovements: SwapHorizIcon,
};

function getButtonVariant(btn) {
  if (btn.variant) return btn.variant;
  if (btn.danger) return 'danger';
  if (btn.success) return 'success';
  if (btn.warning) return 'warning';
  if (btn.className?.includes('closed-month')) return 'danger';
  return 'primary';
}

function renderIcon(btn, className) {
  if (btn.icon && typeof btn.icon === 'function') {
    return <span className={className}>{btn.icon()}</span>;
  }

  if (btn.icon && typeof btn.icon !== 'function') {
    const Comp = btn.icon;
    return (
      <span className={className}>
        <Comp sx={{ fontSize: 18 }} />
      </span>
    );
  }

  const Mapped = LABEL_ICON_MAP[btn.label];
  if (!Mapped) return null;

  return (
    <span className={className}>
      <Mapped sx={{ fontSize: 18 }} />
    </span>
  );
}

export default function NavigationButton({
  t,
  direction,
  selectedRows = [],
  fileInputRef,
  data = [],
}) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const dropdownRef = useRef(null);

  const visibleCount = useMemo(
    () => data.filter((d) => d.show !== false && !(d.showIfSelected && !selectedRows.length)).length,
    [data, selectedRows.length]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpenDropdown(null);
    };

    if (openDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDropdown]);

  const handleDropdownClick = async (btn, idx) => {
    if (btn.isDropdown) {
      setOpenDropdown(openDropdown === idx ? null : idx);
      return;
    }

    try {
      if (btn.loading) {
        setLoadingStates((prev) => ({ ...prev, [idx]: true }));
      }

      const handleClick = () => (btn.isUpload ? btn.onClick(fileInputRef) : btn.onClick());
      const result = handleClick();
      if (result && typeof result.then === 'function') {
        await result;
      }
    } catch (error) {
      console.error('Button action failed:', error);
    } finally {
      if (btn.loading) {
        setLoadingStates((prev) => ({ ...prev, [idx]: false }));
      }
    }
  };

  const handleDropdownItemClick = async (item, btnIdx) => {
    try {
      if (item.loading) {
        setLoadingStates((prev) => ({ ...prev, [`${btnIdx}-item`]: true }));
      }

      const result = item.isUpload ? item.onClick(fileInputRef) : item.onClick();
      if (result && typeof result.then === 'function') {
        await result;
      }
    } catch (error) {
      console.error('Dropdown item action failed:', error);
    } finally {
      if (item.loading) {
        setLoadingStates((prev) => ({ ...prev, [`${btnIdx}-item`]: false }));
      }
      window.setTimeout(() => setOpenDropdown(null), 0);
    }
  };

  return (
    <div
      className={`${styles.container} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
      ref={dropdownRef}
      role="toolbar"
      aria-label="Navigation actions"
    >
      {data.map((btn, idx) => {
        if (btn.show === false || (btn.showIfSelected && selectedRows.length === 0)) {
          return null;
        }

        const visibleDropdownItems = btn.isDropdown
          ? (btn.dropdownItems || []).filter(
              (item) => item.show !== false && !(item.showIfSelected && selectedRows.length === 0)
            )
          : [];

        if (btn.isDropdown && visibleDropdownItems.length === 0) {
          return null;
        }

        const isLoading = loadingStates[idx];
        const variant = getButtonVariant(btn);
        const isOpen = openDropdown === idx;
        const variantClass = styles[variant] || '';

        return (
          <div
            key={idx}
            className={`${styles.wrap} ${isOpen ? styles.selectOpen : ''} ${visibleCount > 8 ? styles.crowded : ''}`}
            style={{
              width: btn.style?.width,
              minWidth: btn.style?.minWidth,
              maxWidth: btn.style?.maxWidth,
              opacity: btn.style?.opacity,
              pointerEvents: btn.style?.pointerEvents,
            }}
          >
            <button
              type="button"
              className={`${styles.trigger} ${variantClass} ${isOpen ? styles.triggerOpen : ''} ${btn.noTruncate ? styles.triggerFull : ''} ${btn.className || ''}`}
              style={{
                width: btn.style?.width,
                minWidth: btn.style?.minWidth,
                maxWidth: btn.style?.maxWidth,
              }}
              onClick={() => handleDropdownClick(btn, idx)}
              disabled={btn.disabled || isLoading}
              aria-expanded={btn.isDropdown ? isOpen : undefined}
              aria-haspopup={btn.isDropdown ? 'menu' : undefined}
              title={btn.tooltip || t(btn.label)}
            >
              {renderIcon(
                btn,
                `${styles.icon} ${btn.iconColor === 'green' ? styles.iconGreen : ''}`
              )}
              <span className={styles.triggerText}>{t(btn.label)}</span>
              {btn.isDropdown && <span className={styles.chevron} aria-hidden="true" />}
              {isLoading && <CircularProgress size={14} className={styles.spinner} />}
            </button>

            {btn.isDropdown && isOpen && (
              <div className={styles.list} role="menu" aria-label={t(btn.label)}>
                {visibleDropdownItems.map((item, itemIdx) => {
                  const itemIsLoading = loadingStates[`${idx}-item`];
                  const isDanger = !!item.danger || item.label === 'BulkDelete';

                  return (
                    <button
                      type="button"
                      key={itemIdx}
                      onClick={() => handleDropdownItemClick(item, idx)}
                      className={`${styles.option}${isDanger ? ` ${styles.optionDanger}` : ''}`}
                      role="menuitem"
                      disabled={item.disabled || itemIsLoading}
                      title={item.tooltip || item.displayLabel || t(item.label)}
                    >
                      {renderIcon(
                        item,
                        `${styles.optionIcon}${isDanger ? ` ${styles.optionIconDanger}` : ''}`
                      )}
                      <span className={styles.optionLabel}>{item.displayLabel || t(item.label)}</span>
                      {item.badge && <span className={styles.badge}>{item.badge}</span>}
                      {itemIsLoading && <CircularProgress size={12} className={styles.spinner} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} aria-hidden="true" />
    </div>
  );
}
